"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return OAuthToken;
    }
});
const _nanoid = require("nanoid");
const _globals = require("~/utils/globals");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _extractProps = require("~/helpers/extractProps");
const _modelUtils = require("~/utils/modelUtils");
const _NocoCache = /*#__PURE__*/ _interop_require_default(require("~/cache/NocoCache"));
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
let OAuthToken = class OAuthToken {
    static async insert(tokenData, ncMeta = _Noco.default.ncMeta) {
        let insertData = (0, _extractProps.extractProps)(tokenData, [
            'fk_client_id',
            'fk_user_id',
            'access_token',
            'access_token_expires_at',
            'refresh_token',
            'refresh_token_expires_at',
            'resource',
            'audience',
            'granted_resources',
            'scope'
        ]);
        insertData.id = (0, _nanoid.nanoid)(20);
        insertData = _extends({}, insertData, {
            is_revoked: false
        });
        insertData = (0, _modelUtils.prepareForDb)(insertData, [
            'granted_resources'
        ]);
        await ncMeta.metaInsert2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, insertData, true);
        return this.getByAccessToken(insertData.access_token, ncMeta).then(async (token)=>{
            await _NocoCache.default.appendToList('root', _globals.CacheScope.OAUTH_TOKEN, [], `${_globals.CacheScope.OAUTH_TOKEN}:${insertData.access_token}`);
            return token;
        });
    }
    static async get(id, ncMeta = _Noco.default.ncMeta) {
        const data = await ncMeta.metaGet(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, {
            id
        });
        return data && this.castType(data);
    }
    static async getByAccessToken(accessToken, ncMeta = _Noco.default.ncMeta) {
        let data = await _NocoCache.default.get('root', `${_globals.CacheScope.OAUTH_TOKEN}:${accessToken}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!data) {
            data = await ncMeta.metaGet(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, {
                access_token: accessToken
            });
            if (data) {
                await _NocoCache.default.set('root', `${_globals.CacheScope.OAUTH_TOKEN}:${accessToken}`, data);
            }
        }
        return data && this.castType(data);
    }
    static async getByRefreshToken(refreshToken, ncMeta = _Noco.default.ncMeta) {
        const data = await ncMeta.metaGet(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, {
            refresh_token: refreshToken
        });
        return data && this.castType(data);
    }
    static async listByUser(userId, ncMeta = _Noco.default.ncMeta) {
        const tokens = await ncMeta.metaList2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, {
            condition: {
                fk_user_id: userId,
                is_revoked: false
            }
        });
        return tokens == null ? void 0 : tokens.map((t)=>this.castType(t));
    }
    static async revoke(id, ncMeta = _Noco.default.ncMeta) {
        const token = await this.get(id, ncMeta);
        if (!token) {
            return false;
        }
        const updateData = {
            is_revoked: true
        };
        await ncMeta.metaUpdate(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, updateData, {
            id
        });
        // Update cache by access token
        await _NocoCache.default.update('root', `${_globals.CacheScope.OAUTH_TOKEN}:${token.access_token}`, updateData);
        return true;
    }
    static async deleteAllByClient(clientId, ncMeta = _Noco.default.ncMeta) {
        const BATCH_SIZE = 100;
        let deletedCount = 0;
        // eslint-disable-next-line no-constant-condition
        while(true){
            // Fetch a batch of tokens
            const tokens = await ncMeta.metaList2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, {
                condition: {
                    fk_client_id: clientId
                },
                limit: BATCH_SIZE
            });
            if (!tokens || tokens.length === 0) {
                break;
            }
            // Clear cache for each token in the batch
            for (const token of tokens){
                await _NocoCache.default.deepDel('root', `${_globals.CacheScope.OAUTH_TOKEN}:${token.access_token}`, _globals.CacheDelDirection.CHILD_TO_PARENT);
            }
            // Delete the batch
            const tokenIdsToDelete = tokens.map((t)=>t.id);
            await ncMeta.metaDelete(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, {
                id: {
                    in: tokenIdsToDelete
                }
            });
            deletedCount += tokens.length;
            // If we got fewer than BATCH_SIZE, we're done
            if (tokens.length < BATCH_SIZE) {
                break;
            }
        }
        return deletedCount;
    }
    static async updateLastUsed(id, ncMeta = _Noco.default.ncMeta) {
        const token = await this.get(id, ncMeta);
        if (!token) {
            return false;
        }
        const updateData = {
            last_used_at: new Date().toISOString()
        };
        await ncMeta.metaUpdate(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_TOKENS, updateData, {
            id
        });
        // Update cache by access token
        await _NocoCache.default.update('root', `${_globals.CacheScope.OAUTH_TOKEN}:${token.access_token}`, updateData);
        return true;
    }
    static castType(token) {
        if (!token) return null;
        token = (0, _modelUtils.prepareForResponse)(token, [
            'granted_resources'
        ]);
        return new OAuthToken(token);
    }
    constructor(token){
        Object.assign(this, token);
    }
};
